import { memo, useState } from 'react';
import { Role } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  ChecklistField,
  useFormField,
  CheckboxField,
  setValue,
  CustomChangeEvent,
  Text,
  Button,
} from '@frontend/design-system';
import { UserRoleQuickLinks } from './user-role-quick-links';

export const PermissionSelectionSection = ({
  rolesByCategory,
  rolesByName,
  rolesForLocation,
  onChange,
  isParentLocation,
  isUnify,
  hasNewUsersManagement,
}: {
  rolesByCategory: Record<string, Role[]>;
  rolesByName: Record<string, Role>;
  rolesForLocation: string[];
  onChange: (val: string[]) => void;
  isParentLocation: boolean;
  isUnify: boolean;
  hasNewUsersManagement: boolean;
}) => {
  return hasNewUsersManagement ? (
    <PermissionSelectionContent
      rolesByCategory={rolesByCategory}
      rolesByName={rolesByName}
      rolesForLocation={rolesForLocation}
      onChange={onChange}
      isParentLocation={isParentLocation}
      isUnify={isUnify}
    />
  ) : (
    <LegacyPermissionSelectionContent
      rolesByCategory={{ Legacy: rolesByCategory.Legacy }}
      rolesByName={rolesByName}
      rolesForLocation={rolesForLocation}
      onChange={onChange}
      isParentLocation={isParentLocation}
      isUnify={isUnify}
    />
  );
};

const PermissionSelectionContent = ({
  rolesByCategory,
  rolesByName,
  rolesForLocation,
  onChange,
  isParentLocation,
  isUnify,
}: {
  rolesByCategory: Record<string, Role[]>;
  rolesByName: Record<string, Role>;
  rolesForLocation: string[];
  onChange: (val: string[]) => void;
  isParentLocation: boolean;
  isUnify: boolean;
}) => {
  const { t } = useTranslation('account');

  const [selected, setSelected] = useState<typeof rolesByCategory>(() =>
    convertListToCategories({
      template: rolesForLocation,
      rolesByName,
      rolesByCategory,
    })
  );

  const onPermissionChange = ({ category, value }: { category: string; value: string[] }) => {
    const updated = { ...selected, [category]: value.map((role) => rolesByName[role]) };

    setSelected(updated);
    onChange(
      Object.values(updated)
        .flat()
        .map((role) => role.name ?? '')
    );
  };

  return (
    <div style={{ display: 'grid', gap: theme.spacing(2) }}>
      {!isParentLocation && (
        <div style={{ display: 'grid', gap: theme.spacing(2) }}>
          <div>
            <Text weight='bold'>{t('Quick Select Options')}</Text>
            <Text color='subdued' size='medium'>
              {t('Get started quickly with one of these curated options.')}
            </Text>
          </div>
          <UserRoleQuickLinks
            onChange={(roles) => {
              setSelected(convertListToCategories({ template: roles, rolesByName, rolesByCategory }));
              onChange(roles);
            }}
          />
        </div>
      )}
      <div style={{ borderBottom: `1px solid ${theme.colors.neutral20}` }} />
      <div style={{ display: 'grid', gap: theme.spacing(2) }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', justifyContent: 'space-between' }}>
          <div>
            <Text weight='bold'>{t('Individual Permissions')}</Text>
            <Text color='subdued' size='medium'>
              {t("Customize this user's permissions.")}
            </Text>
          </div>
          <Button
            variant='tertiary'
            disabled={Object.values(selected).length === 0}
            onClick={() => {
              setSelected({});
              onChange([]);
            }}
          >
            {t('Clear')}
          </Button>
        </div>

        {isParentLocation ? (
          <ParentLocationPermissionsList
            activeRoles={selected}
            rolesByCategory={rolesByCategory}
            onChange={onPermissionChange}
          />
        ) : (
          <PermissionsList activeRoles={selected} rolesByCategory={rolesByCategory} onChange={onPermissionChange} />
        )}

        <div style={{ borderBottom: `1px solid ${theme.colors.neutral20}` }} />

        <div>
          <Text weight='bold'>{t('Legacy Permissions')}</Text>
          <Text color='subdued' size='medium'>
            {t("Customize this user's permissions using our previous (older) permissions system.")}
          </Text>
        </div>
        <LegacyPermissionsList
          activeRoles={selected}
          rolesByCategory={{ Legacy: rolesByCategory.Legacy }}
          isParentLocation={isParentLocation}
          onChange={onPermissionChange}
          isUnify={isUnify}
        />
      </div>
    </div>
  );
};

const LegacyPermissionSelectionContent = ({
  rolesByCategory,
  rolesByName,
  rolesForLocation,
  onChange,
  isParentLocation,
  isUnify,
}: {
  rolesByCategory: Record<string, Role[]>;
  rolesByName: Record<string, Role>;
  rolesForLocation: string[];
  onChange: (val: string[]) => void;
  isParentLocation: boolean;
  isUnify: boolean;
}) => {
  const { t } = useTranslation('account');

  const [selected, setSelected] = useState<typeof rolesByCategory>(() =>
    convertListToCategories({
      template: rolesForLocation,
      rolesByName,
      rolesByCategory,
    })
  );

  return (
    <div style={{ display: 'grid', gap: theme.spacing(2) }}>
      <div>
        <Text weight='bold'>{t('Legacy Permissions')}</Text>
        <Text color='subdued' size='medium'>
          {t("Customize this user's permissions using our previous (older) permissions system.")}
        </Text>
      </div>
      <LegacyPermissionsList
        activeRoles={selected}
        rolesByCategory={rolesByCategory}
        isParentLocation={isParentLocation}
        onChange={({ category, value }) => {
          const updated = { ...selected, [category]: value.map((role) => rolesByName[role]) };
          setSelected(updated);
          onChange(
            Object.values(updated)
              .flat()
              .map((role) => role.name ?? '')
          );
        }}
        isUnify={isUnify}
      />
    </div>
  );
};

const LegacyPermissionsListContent = ({
  options,
  selectedOptions,
  onChange,
}: {
  options: { label: string; value: string; description?: string }[];
  selectedOptions: string[];
  onChange: (val: string[]) => void;
}) => {
  const checklistField = useFormField(
    {
      type: 'checklist',
      value: selectedOptions,
    },
    [selectedOptions]
  );
  return (
    <ChecklistField
      {...checklistField}
      onChange={(e) => {
        checklistField.onChange(e);
        // @ts-ignore - fields types are not working correctly
        const field = setValue({ ...checklistField, type: 'checklist' }, e.value);
        onChange(field.value);
      }}
      name='legacy-permissions'
      label=''
      css={{
        paddingLeft: theme.spacing(3),
        '> div': {
          marginBottom: theme.spacing(1),
        },
        // There is no label to display, but it is taking up space
        '> label': {
          display: 'none',
        },
      }}
    >
      {options.map((option) => (
        <ChecklistField.Option key={option.value} name={option.value}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto auto',
              alignItems: 'baseline',
              gap: theme.spacing(1),
            }}
          >
            {option.label}
            <Text color='subdued' size='small'>
              {option.description}
            </Text>
          </div>
        </ChecklistField.Option>
      ))}
    </ChecklistField>
  );
};

const ParentLocationPermissionsList = ({
  activeRoles,
  rolesByCategory,
  onChange,
}: {
  activeRoles: Record<string, Role[]>;
  rolesByCategory: Record<string, Role[]>;
  onChange: (val: { category: string; value: string[] }) => void;
}) => {
  // Remove the Legacy category from the list of categories
  const newCategories = {
    'Data Sync': rolesByCategory['Data Sync'],
    'Location Settings': rolesByCategory['Location Settings'],
    Phones: rolesByCategory['Phones'],
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: theme.spacing(2),
      }}
    >
      {Object.entries(newCategories).map(([category, rolesInCategory]) => {
        const options = rolesInCategory.map((role) => ({
          label: role.name === 'Phone Admin' ? 'Manage Phone System' : role.name ?? '',
          value: role.name ?? '',
        }));

        const selectedOptions = options
          .filter((option) =>
            activeRoles[category] ? activeRoles[category].find((role) => role.name === option.value) : false
          )
          .map((option) => option.value);

        return (
          <NestedChecklistField
            key={category}
            category={category}
            options={options}
            onChange={(val) => onChange({ category, value: val })}
            selectedOptions={selectedOptions}
          />
        );
      })}
    </div>
  );
};

const LegacyPermissionsList = ({
  activeRoles,
  rolesByCategory,
  onChange,
  isParentLocation,
  isUnify,
}: {
  activeRoles: Record<string, Role[]>;
  rolesByCategory: Record<string, Role[]>;
  onChange: (val: { category: string; value: string[] }) => void;
  isParentLocation: boolean;
  isUnify: boolean;
}) => {
  const category = 'Legacy';
  const { t } = useTranslation('account');

  // This is only for what we show to the user, do not use this for values that are returned from the backend
  const SUPER_ADMIN_LABEL = t('Super Admin');

  const options = isParentLocation
    ? [
        { label: SUPER_ADMIN_LABEL, value: 'Super Admin', description: t('Full access to all features and settings') },
        isUnify ? { label: 'Phones Manager', value: 'Phones Manager' } : null,
      ].filter(Boolean)
    : rolesByCategory[category]
        .map((role) => ({
          label: role.name ?? '',
          value: role.name ?? '',
          description: role.name === 'Super Admin' ? t('Full access to all features and settings') : undefined,
        }))
        // Sort Super Admin to the top
        .sort((a, b) => (a.label === SUPER_ADMIN_LABEL ? -1 : b.label === SUPER_ADMIN_LABEL ? 1 : 0));

  const selectedOptions = options
    .filter((option) =>
      activeRoles[category] ? activeRoles[category].find((role) => role.name === option.value) : false
    )
    .map((option) => option.value);

  return (
    <LegacyPermissionsListContent
      options={options}
      selectedOptions={selectedOptions}
      onChange={(val) => onChange({ category, value: val })}
    />
  );
};

const PermissionsList = ({
  activeRoles,
  rolesByCategory,
  onChange,
}: {
  activeRoles: Record<string, Role[]>;
  rolesByCategory: Record<string, Role[]>;
  onChange: (val: { category: string; value: string[] }) => void;
}) => {
  // Remove the Legacy category from the list of categories
  const newCategories = { ...rolesByCategory };
  delete newCategories.Legacy;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: theme.spacing(2),
      }}
    >
      {Object.entries(newCategories).map(([category, rolesInCategory]) => {
        const options = rolesInCategory.map((role) => ({
          label: role.name === 'Phone Admin' ? 'Manage Phone System' : role.name ?? '',
          value: role.name ?? '',
        }));

        const selectedOptions = options
          .filter((option) =>
            activeRoles[category] ? activeRoles[category].find((role) => role.name === option.value) : false
          )
          .map((option) => option.value);

        return (
          <NestedChecklistField
            key={category}
            category={category}
            options={options}
            onChange={(val) => onChange({ category, value: val })}
            selectedOptions={selectedOptions}
          />
        );
      })}
    </div>
  );
};

const NestedChecklistField = memo(
  ({
    category,
    selectedOptions,
    options,
    onChange,
  }: {
    category: string;
    selectedOptions: string[];
    options: { label: string; value: string }[];
    onChange: (val: string[]) => void;
  }) => {
    const checklistField = useFormField(
      {
        type: 'checklist',
        value: selectedOptions,
      },
      [selectedOptions]
    );
    const checkboxField = useFormField({ type: 'checkbox', value: checklistField.value.length > 0 });

    return (
      <div style={{ display: 'grid', gap: theme.spacing(1), gridTemplateRows: 'auto 1fr' }}>
        <CheckboxField
          {...checkboxField}
          indeterminate={checklistField.value.length > 0 && checklistField.value.length < options.length}
          value={checklistField.value.length > 0}
          // @ts-ignore - fields types are not corking correctly
          onChange={(e: CustomChangeEvent<string[]>) => {
            let field;
            if (e.value) {
              checklistField.onChange({ ...e, value: [] });
              checklistField.onChange({ ...e, value: options.map((option) => option.value) });
              field = setValue(
                { ...checklistField, type: 'checklist' },
                options.map((option) => option.value)
              );
            } else {
              checklistField.onChange({ ...e, value: [] });
              field = setValue({ ...checklistField, type: 'checklist' }, []);
            }

            /**
             * Sync the value of the checklist field with the parent field
             */
            onChange(field.value);
          }}
          label={category}
          name={category}
          css={{
            label: {
              fontWeight: theme.font.weight.bold,
            },
          }}
        />
        <ChecklistField
          {...checklistField}
          onChange={(e) => {
            checklistField.onChange(e);
            // @ts-ignore - fields types are not corking correctly
            const field = setValue({ ...checklistField, type: 'checklist' }, e.value);
            onChange(field.value);
          }}
          name=''
          label=''
          css={{
            paddingLeft: theme.spacing(3),
            '> div': {
              marginBottom: theme.spacing(1),
            },
            // There is no label to display, but it is taking up space
            '> label': {
              display: 'none',
            },
          }}
        >
          {options.map((option) => (
            <ChecklistField.Option key={option.value} name={option.value}>
              {option.label}
            </ChecklistField.Option>
          ))}
        </ChecklistField>
      </div>
    );
  }
);

const convertListToCategories = ({
  template,
  rolesByName,
  rolesByCategory,
}: {
  template: string[];
  rolesByName: Record<string, Role>;
  rolesByCategory: Record<string, Role[]>;
}) => {
  if (template.includes('Super Admin')) {
    return {
      ...rolesByCategory,
      Legacy: [rolesByName['Super Admin']],
    };
  }

  return template.reduce((acc, role) => {
    const roleItem = rolesByName[role];

    // There might be a role that is not in the list of roles
    if (!roleItem) {
      return acc;
    }

    const category = roleItem.category ?? 'default';
    if (!acc[category]) {
      acc[category] = [roleItem];
    } else {
      acc[category].push(roleItem);
    }
    return acc;
  }, {} as Record<string, Role[]>);
};
